import { useContext, useEffect } from 'react'
import { useLocation } from 'react-router'
import { observer } from 'mobx-react-lite'
import { isAxiosError } from 'axios'
import styled from 'styled-components'

import { MODALS_ID } from '../../../constants'
import type { components } from '../../../constants/schema'

import {
  requestIdentityVerification,
  requestIdentify,
  convertIdentityVerificationResult,
} from '../../../utils/identityVerification'

import { ModalContext } from '../../../provider/modalProvider'
import { StoresContext } from '../../../provider/StoreProvider'

import { useAlert } from '../../../hooks'
import { useIsMobile } from '../../../hooks/useIsMobile'

import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { Button } from '../../../components/button'
import { Text } from '../../../components/text'
import { Footer } from '../../../components/footer'
import { Header } from '../../../components/header'
import { AlreadyVerificationModal, MismatchUserModal, SuccessVerificationModal } from '../modal'
import type { IdentifyInfo } from '../modal/AlreadyVerificationModal'

import { Color } from '../../../theme/color'

export const PhoneVerification = observer(() => {
  const {
    authStore: { userInfo },
    commonStore,
  } = useContext(StoresContext)
  const { openModal } = useContext(ModalContext)
  const phoneNumber = userInfo?.identification?.danalPhoneNumber

  const location = useLocation()
  const openAlert = useAlert()
  const isMobile = useIsMobile()

  useEffect(() => {
    commonStore.setShowTab(false)
  }, [])

  useEffect(() => {
    if (localStorage.getItem('isVerification') !== 'true') return

    if (location.search.includes('identityVerificationId')) {
      // TODO: 에러 메시지 보여주기
      const convertQuery = convertIdentityVerificationResult(location.search.substring(1))
      identify(convertQuery)
    }
  }, [location])

  const requestVerification = async () => {
    const response = await requestIdentityVerification(`${window.location.origin}/personalInfo/phone`)

    if (!isMobile && response && response.code !== 'FAILURE_TYPE_PG') {
      identify(response as components['schemas']['ReqQueryIdentication'])
    }
  }

  const identify = async (data: components['schemas']['ReqQueryIdentication']) => {
    const response = await requestIdentify(data)
    if (isAxiosError(response)) {
      const code = response.response?.data.CODE

      switch (code) {
        // 이미 인증한 유저
        case 'IDNTY_050005': {
          const info: IdentifyInfo = response.response?.data?.info
          if (!info) return
          openModal({ id: MODALS_ID.ALREADY_VERIFICATION, Component: () => <AlreadyVerificationModal info={info} /> })
          return
        }
        // 휴대폰이 본인 명의가 아닌 것
        case 'IDNTY_050006': {
          openModal({ id: MODALS_ID.MISMATCH_USER, Component: MismatchUserModal })
          return
        }
        // Yes24에서 제공받은 이름과 본인인증에서의 이름이 다름
        case 'IDNTY_050007': {
          openAlert({
            content: (
              <Text size={16} weight={500} lineHeight={24} $textAlign="center">
                로그인 시 제공받은 명의와 일치하지 않아요
              </Text>
            ),
          })
          return
        }
        default: {
          openAlert({
            content: (
              <Text size={16} weight={500} lineHeight={24} $textAlign="center">
                알 수 없는 이유로 본인인증에 실패했어요.
                <br />
                문제가 계속될 경우 1:1 문의를 통해 문의해 주세요.
              </Text>
            ),
          })
          return
        }
      }
    }

    openModal({ id: 'ALERT', Component: SuccessVerificationModal })
  }

  return (
    <Wrap>
      <Header type="SUB" title="휴대폰 번호" href={location.search ? '/personalInfo' : undefined} />
      <Content>
        <Container>
          <Text size={24} weight={600} lineHeight={34}>
            이동통신사 본인인증을 통해
            <br />
            휴대폰 번호를 등록 또는 변경하실 수 있어요.
          </Text>
          <InputBox onClick={requestVerification}>
            {phoneNumber
              ? `${userInfo?.identification?.danalPhoneNumber?.substring(
                  0,
                  3,
                )}****${userInfo?.identification?.danalPhoneNumber?.substring(7, 11)}`
              : '휴대폰 번호를 등록해 주세요.'}
          </InputBox>
        </Container>
      </Content>
      <Footer>
        <Button type="primary" size="large" block={true} onClick={requestVerification}>
          {phoneNumber ? '변경하기' : '등록하기'}
        </Button>
      </Footer>
    </Wrap>
  )
})

const Container = styled.div`
  padding: 56px 24px 24px;
  ion-item {
    overflow: hidden;
    border: 1px solid #ebebeb;
    border-radius: 8px;
  }
`

const InputBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  margin-top: 34px;
  padding: 16px;
  border: 1px solid ${Color.gray_3};
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${Color.gray_4};
`
